import { useState, type FormEvent } from 'react';
import { C, R, font } from '../theme/tokens';
import { Btn, FieldError, FieldLabel, Mono, Tag, Toggle, inputStyle, textareaStyle } from '../components/ui';
import { saveFirstProfile, type ProfileDraft } from '../services/session';
import { isDemo } from '../lib/clock';
import { useApp } from '../state/store';
import { orderedSeries } from '../domain/series';
import { AffiliationChips, PrimarySelect, toggleAffiliation } from '../components/affiliations';

/** First successful sign-in: one short screen, then Home. Skippable. */
export function FirstProfile({ initialName, onDone }: { initialName: string; onDone: (d: ProfileDraft | null) => void }) {
  const [name, setName] = useState(initialName);
  const [city, setCity] = useState('');
  // Series come from the catalogue (launch series first), never a hard-coded list.
  const catalogue = orderedSeries(useApp().state.data.series);
  // Zero or more; none is pre-selected, so nobody is placed in a series they didn't choose.
  const [affs, setAffs] = useState<{ list: string[]; primary: string }>({ list: [], primary: '' });
  const [bio, setBio] = useState('');
  const [publicProfile, setPublic] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (name.trim().length < 2) err.name = 'Add the name ambassadors will see.';
    if (city.trim().length < 2) err.city = 'Add your home city.';
    setErrors(err);
    if (Object.keys(err).length) return;
    const draft = { name: name.trim(), city: city.trim(), affiliations: affs.list, primary: affs.primary, bio: bio.trim(), publicProfile };
    setSaving(true);
    try {
      await saveFirstProfile(draft);
      onDone(draft);
    } catch {
      setSaving(false);
      setErrors({ form: 'We couldn’t save your profile. Please try again.' });
    }
  }

  return (
    <div className="signin-check" style={{ alignItems: 'flex-start', overflowY: 'auto', background: C.paper }}>
      <form onSubmit={submit} noValidate className="fade" style={{ width: '100%', maxWidth: 480, margin: '0 auto', padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 4px calc(env(safe-area-inset-bottom, 0px) + 28px)', display: 'flex', flexDirection: 'column', gap: 16, color: C.ink }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <img src={`${import.meta.env.BASE_URL}brand/unit-mark-violet.svg`} alt="UNIT" style={{ height: 22, width: 'auto' }} />
          {isDemo && <Tag label="DEMO" bg={C.amberBg} fg={C.amber} />}
        </div>
        <span style={{ font: font(600, 10), letterSpacing: '.26em', color: C.violet }}>WELCOME, AMBASSADOR</span>
        <h1 style={{ margin: 0, font: font(800, 44, 0.95), letterSpacing: '-.045em' }}>Set up your profile.</h1>
        <p style={{ margin: 0, font: font(400, 15, 1.45), color: C.grey }}>Thirty seconds. You can change any of this later in Me.</p>

        <div style={{ background: C.white, border: `1px solid ${C.subtle}`, borderRadius: R.card, padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FieldLabel htmlFor="fp-name">Name</FieldLabel>
            <input id="fp-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoComplete="name" aria-invalid={Boolean(errors.name)} style={inputStyle} />
            {errors.name && <FieldError>{errors.name}</FieldError>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FieldLabel htmlFor="fp-city">Home city</FieldLabel>
            <input id="fp-city" value={city} onChange={(e) => setCity(e.target.value)} maxLength={60} placeholder="London" autoComplete="address-level2" aria-invalid={Boolean(errors.city)} style={inputStyle} />
            {errors.city && <FieldError>{errors.city}</FieldError>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FieldLabel>Your UNITs (choose any)</FieldLabel>
            <AffiliationChips label="Your UNITs" items={catalogue} selected={affs.list} primary={affs.primary} onToggle={(id) => setAffs((a) => toggleAffiliation(a.list, a.primary, id))} />
            <PrimarySelect catalogue={catalogue} selected={affs.list} primary={affs.primary} onChange={(primary) => setAffs((a) => ({ ...a, primary }))} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FieldLabel htmlFor="fp-bio">Introduce yourself (optional)</FieldLabel>
            <textarea id="fp-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} maxLength={300} placeholder="What you love organising in your city." style={textareaStyle} />
            <Mono size={10} style={{ alignSelf: 'flex-end' }}>{`${bio.length} / 300`}</Mono>
          </div>
          <Toggle on={publicProfile} onToggle={() => setPublic((x) => !x)} label="Public profile" sub="Off: only your activity teams see your story. Email is never shown." />
          {errors.form && <FieldError>{errors.form}</FieldError>}
          <Btn type="submit" variant="primary" size="lg" full disabled={saving} aria-busy={saving}>{saving ? 'Saving…' : 'Enter UGAIN'}</Btn>
        </div>
        <button type="button" onClick={() => onDone(null)} style={{ alignSelf: 'center', border: 0, background: 'transparent', color: C.violet, font: font(600, 14), padding: '12px 8px' }}>Skip for now</button>
      </form>
    </div>
  );
}
